import React, { useState } from "react";
import styles from "./styles.module.scss";
import LilliSelect, { LilliSelectOption } from "../../Components/LilliDesignSystem/LilliSelect";
import PrimaryButton from "../../Components/PrimaryButton";
import { postHubInstallStatus } from "@intelligentlilli/api-layer";
import { FAILURE_REASON_OPTIONS, FailureReason } from "../../Services/Constants/installFail";
import { useDispatch } from "react-redux";
import { setRefreshOrganisationHubs } from "../../State/slices/session-slice";
import { useInstallTracking } from "../../Pages/Installation/InstallWrapper";
import { getLEDGuardKey, getLEDCacheKey } from "../../Services/Constants";

interface InstallFailedFormProps {
  onComplete: () => void;
  server: string;
  hubId: string;
  installStage?: string;
  residentId?: string;
  devicestring?: string;
}

const InstallFailedForm = ({ onComplete, server, hubId, installStage, residentId, devicestring }: InstallFailedFormProps) => {
  const dispatch = useDispatch();
  const { trackInstall } = useInstallTracking();
  const [selectedReason, setSelectedReason] = useState<LilliSelectOption | null>(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<"success" | "error" | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);
    setSubmitResult(null);
    setResponseError(null);
    try {
      const result = await postHubInstallStatus(server, hubId, {
        status: "failed",
        failureReason: selectedReason.value as FailureReason,
        installationNotes: additionalContext,
      }, "web");
      if (result.status === 200) {
        setSubmitResult("success");
        trackInstall({
          action: "exit_install",
          failure_reason: `${selectedReason.value}`,
          ...(installStage && { install_stage: installStage }),
          ...(residentId && { residentId }),
        });
        if (devicestring) {
          localStorage.removeItem(getLEDGuardKey(devicestring));
          localStorage.removeItem(getLEDCacheKey(devicestring));
        }
        setTimeout(() => {
          dispatch(setRefreshOrganisationHubs(true));
          onComplete();
        }, 2000);
      } else {
        setSubmitResult("error");
        if (result.status === 401) {
          setResponseError("Status 401: Unauthorised. Try logging out and back in.");
        } else if (result.status === 403) {
          setResponseError("Status 403: Forbidden. You don't have permission to complete this action.");
        } else {
          setResponseError(`Status ${result.status}: Something went wrong, please try again.`);
        }
      }
    } catch (error) {
      console.error("Failed to post install status", error);
      setSubmitResult("error");
      setResponseError("Something went wrong, please try again.")
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Install failed to proceed</h2>
      <p className={styles.subtitle}>
        If install could not proceed, please tell us what happened.
      </p>
      <p className={styles.subtitle}>
        Progress will not be saved when exiting install.
        To resolve any issues, please contact support at +44 (0)758
        800 8527 or email support@intelligentlilli.com.
      </p>
      <div>
        <div className={styles.label}>Reason</div>
        <LilliSelect
          name="install-failed-reason"
          options={FAILURE_REASON_OPTIONS}
          value={selectedReason}
          onChange={setSelectedReason}
          placeholder="Select a reason"
          withinModal={true}
          maxMenuHeight={220}
        />
      </div>
      <div>
        <div className={styles.label}>
          Tell us why install couldn't proceed (optional).
          This helps us improve our processes.
        </div>
        <textarea
          id="install-failed-context"
          className={styles.textarea}
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
        />
      </div>
      {submitResult === "success" && (
        <p className={styles.success_message}>Submitted successfully.</p>
      )}
      {submitResult === "error" && (
        <p className={styles.error_message}>{responseError}</p>
      )}
      <PrimaryButton
        className={styles.submit_button}
        onClick={handleSubmit}
        disabled={!selectedReason || isSubmitting || submitResult === "success"}
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </PrimaryButton>
    </div>
  );
};

export default InstallFailedForm;
